import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Users, Pencil, Trash2, FileText, Building2, ImagePlus, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { AdminAccessGate } from "@/components/admin-access-gate";
import {
  useContractTemplates,
  useCreateContractTemplate,
  useUpdateContractTemplate,
  useDeleteContractTemplate,
  useContractSignatories,
  useCreateContractSignatory,
  useUpdateContractSignatory,
  useDeleteContractSignatory,
  useContractCompany,
  queryKeys,
} from "@/hooks/queries";
import { GpsDriveApiError, resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import { gpsDriveContractsService, type ContractCompanyInput } from "@/services/gpsdrive-contracts.service";
import type {
  ContractSignatory,
  ContractSignatoryRole,
  ContractTemplate,
} from "@/services/gpsdrive-contracts.service";

export const Route = createFileRoute("/admin/contract")({
  component: () => (
    <AdminAccessGate action="conta_integracoes">
      <ContractPage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Contrato — Administração — Wp Rastreamento" }] }),
});

function ContractPage() {
  const { data: templates, isLoading } = useContractTemplates();

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Contrato</h1>
          <p className="text-sm text-muted-foreground">
            Dados da empresa, modelos de contrato e os signatários/testemunhas que assinam pela empresa.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SignatoriesDialog />
          <TemplateFormDialog
            trigger={
              <Button>
                <Plus className="h-4 w-4" />
                Criar contrato
              </Button>
            }
          />
        </div>
      </div>

      <CompanyInfoCard />

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : !templates || templates.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <FileText className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Nenhum modelo de contrato criado ainda</p>
            <p className="text-xs text-muted-foreground">
              Clique em "Criar contrato" para cadastrar o título e o conteúdo do primeiro modelo.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <TemplateCard key={template.id} template={template} />
          ))}
        </div>
      )}
    </div>
  );
}

// Dados da empresa no cabeçalho do contrato (pedido 2026-10-14: "hoje
// aparece GPS Drive; tem que poder colocar nome da empresa, telefone,
// e-mail, logotipo"). Backend: src/contracts/contract-company.ts.
const COMPANY_FIELDS: { key: keyof ContractCompanyInput; label: string; placeholder: string; wide?: boolean }[] = [
  { key: "name", label: "Nome da empresa / razão social", placeholder: "Ex.: WP Rastreamento LTDA", wide: true },
  { key: "document", label: "CNPJ / CPF", placeholder: "00.000.000/0001-00" },
  { key: "phone", label: "Telefone / WhatsApp", placeholder: "(51) 99999-0000" },
  { key: "email", label: "E-mail", placeholder: "contato@empresa.com.br" },
  { key: "website", label: "Site", placeholder: "www.empresa.com.br" },
  { key: "address", label: "Endereço", placeholder: "Rua, número — bairro — cidade/UF — CEP", wide: true },
];

const EMPTY_COMPANY: ContractCompanyInput = { name: "", document: "", phone: "", email: "", address: "", website: "" };

function CompanyInfoCard() {
  const qc = useQueryClient();
  const { data: company, isLoading } = useContractCompany();
  const [form, setForm] = useState<ContractCompanyInput>(EMPTY_COMPANY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!company) return;
    setForm({
      name: company.name,
      document: company.document,
      phone: company.phone,
      email: company.email,
      address: company.address,
      website: company.website,
    });
  }, [company]);

  const dirty =
    !!company && (Object.keys(EMPTY_COMPANY) as (keyof ContractCompanyInput)[]).some((k) => form[k] !== company[k]);

  const apply = (next: Awaited<ReturnType<typeof gpsDriveContractsService.getCompany>>) =>
    qc.setQueryData(queryKeys.contractCompany, next);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      apply(await gpsDriveContractsService.saveCompany(form));
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof GpsDriveApiError ? err.message : "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      apply(await gpsDriveContractsService.uploadCompanyLogo(file));
    } catch (err) {
      setError(err instanceof GpsDriveApiError ? err.message : "Não foi possível enviar o logotipo.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    setUploading(true);
    setError(null);
    try {
      apply(await gpsDriveContractsService.removeCompanyLogo());
    } catch (err) {
      setError(err instanceof GpsDriveApiError ? err.message : "Não foi possível remover o logotipo.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Building2 className="h-4 w-4" /> Dados da empresa no contrato
        </CardTitle>
        <CardDescription>
          Aparecem no topo do contrato (PDF e página de assinatura do cliente). Campo vazio não aparece.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-[180px_1fr]">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Logotipo</Label>
              <div className="grid h-28 place-items-center rounded-lg border border-dashed border-border bg-white p-2">
                {company?.logoUrl ? (
                  <img
                    src={resolveGpsDriveUrl(company.logoUrl)}
                    alt="Logotipo do contrato"
                    className="max-h-24 max-w-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">Sem logotipo</span>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
              />
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                {company?.customLogo ? "Trocar logotipo" : "Enviar logotipo"}
              </Button>
              {company?.customLogo ? (
                <Button variant="ghost" size="sm" className="w-full text-muted-foreground" disabled={uploading} onClick={removeLogo}>
                  Usar o da Identidade Visual
                </Button>
              ) : (
                company?.logoUrl && (
                  <p className="text-[11px] text-muted-foreground">Usando o logotipo da Identidade Visual.</p>
                )
              )}
            </div>

            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                {COMPANY_FIELDS.map((f) => (
                  <div key={f.key} className={f.wide ? "sm:col-span-2" : undefined}>
                    <Label className="mb-1 block text-xs text-muted-foreground">{f.label}</Label>
                    <Input
                      value={form[f.key]}
                      placeholder={f.placeholder}
                      onChange={(e) => {
                        setForm((p) => ({ ...p, [f.key]: e.target.value }));
                        setSavedAt(null);
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center justify-end gap-3">
                {error && <span className="text-xs text-destructive">{error}</span>}
                {!dirty && savedAt && (
                  <span className="flex items-center gap-1 text-xs text-emerald-600">
                    <Check className="h-3.5 w-3.5" /> Salvo
                  </span>
                )}
                <Button onClick={save} disabled={saving || !dirty}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Salvar dados da empresa
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TemplateCard({ template }: { template: ContractTemplate }) {
  const deleteTemplate = useDeleteContractTemplate();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteTemplate.mutateAsync(template.id);
      toast.success("Modelo de contrato excluído");
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao excluir o modelo");
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="flex-1">
        <CardTitle className="text-base">{template.title}</CardTitle>
        <CardDescription className="line-clamp-4 whitespace-pre-line">{template.content}</CardDescription>
      </CardHeader>
      <CardContent className="flex items-center justify-end gap-2 pt-0">
        <TemplateFormDialog
          template={template}
          trigger={
            <Button variant="outline" size="sm">
              <Pencil className="h-3.5 w-3.5" />
              Editar
            </Button>
          }
        />
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)}>
            <Trash2 className="h-3.5 w-3.5" />
            Excluir
          </Button>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir "{template.title}"?</AlertDialogTitle>
              <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleteTemplate.isPending}>
                {deleteTemplate.isPending ? "Excluindo…" : "Excluir"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

function TemplateFormDialog({
  template,
  trigger,
}: {
  template?: ContractTemplate;
  trigger: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const createTemplate = useCreateContractTemplate();
  const updateTemplate = useUpdateContractTemplate();
  const isEditing = Boolean(template);
  const isPending = createTemplate.isPending || updateTemplate.isPending;

  useEffect(() => {
    if (!open) return;
    setTitle(template?.title ?? "");
    setContent(template?.content ?? "");
  }, [open, template]);

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error("Preencha o título e o conteúdo do contrato");
      return;
    }
    try {
      if (template) {
        await updateTemplate.mutateAsync({ id: template.id, input: { title: title.trim(), content: content.trim() } });
        toast.success("Modelo de contrato atualizado");
      } else {
        await createTemplate.mutateAsync({ title: title.trim(), content: content.trim() });
        toast.success("Modelo de contrato criado");
      }
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao salvar o modelo de contrato");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Editar modelo de contrato" : "Criar modelo de contrato"}</DialogTitle>
          <DialogDescription>
            O conteúdo aceita texto livre — use para o corpo do contrato que o cliente vai assinar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="contract-title">Título</Label>
            <Input
              id="contract-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Contrato de Prestação de Serviços"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="contract-content">Conteúdo do contrato</Label>
            <Textarea
              id="contract-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={12}
              placeholder="Texto completo do contrato…"
            />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SIGNATORY_ROLE_LABEL: Record<ContractSignatoryRole, string> = {
  signatario: "Signatário",
  testemunha: "Testemunha",
};

interface SignatoryFormState {
  name: string;
  role: ContractSignatoryRole;
  cpfCnpj: string;
  email: string;
  phone: string;
}

const EMPTY_SIGNATORY_FORM: SignatoryFormState = {
  name: "",
  role: "signatario",
  cpfCnpj: "",
  email: "",
  phone: "",
};


function SignatoriesDialog() {
  const [open, setOpen] = useState(false);
  const { data: signatories, isLoading } = useContractSignatories();
  const createSignatory = useCreateContractSignatory();
  const updateSignatory = useUpdateContractSignatory();
  const deleteSignatory = useDeleteContractSignatory();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<SignatoryFormState>(EMPTY_SIGNATORY_FORM);

  useEffect(() => {
    if (!open) {
      setEditingId(null);
      setForm(EMPTY_SIGNATORY_FORM);
    }
  }, [open]);

  const set = <K extends keyof SignatoryFormState>(key: K, value: SignatoryFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const startEdit = (signatory: ContractSignatory) => {
    setEditingId(signatory.id);
    setForm({
      name: signatory.name,
      role: signatory.role,
      cpfCnpj: signatory.cpfCnpj ?? "",
      email: signatory.email ?? "",
      phone: signatory.phone ?? "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_SIGNATORY_FORM);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Informe o nome");
      return;
    }
    const input = {
      name: form.name.trim(),
      role: form.role,
      cpfCnpj: form.cpfCnpj.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
    };
    try {
      if (editingId) {
        await updateSignatory.mutateAsync({ id: editingId, input });
        toast.success("Cadastro atualizado");
      } else {
        await createSignatory.mutateAsync(input);
        toast.success("Signatário/testemunha cadastrado");
      }
      cancelEdit();
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao salvar o cadastro");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteSignatory.mutateAsync(id);
      toast.success("Cadastro excluído");
      if (editingId === id) cancelEdit();
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao excluir o cadastro");
    }
  };

  const isSaving = createSignatory.isPending || updateSignatory.isPending;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" title="Signatários e testemunhas" aria-label="Signatários e testemunhas">
          <Users className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Signatários e testemunhas</DialogTitle>
          <DialogDescription>
            Pessoas cadastradas para assinar os contratos pela empresa, como signatário ou testemunha.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="signatory-name">Nome</Label>
            <Input id="signatory-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Papel</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v as ContractSignatoryRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="signatario">Signatário</SelectItem>
                <SelectItem value="testemunha">Testemunha</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="signatory-cpf">CPF/CNPJ</Label>
            <Input id="signatory-cpf" value={form.cpfCnpj} onChange={(e) => set("cpfCnpj", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="signatory-email">E-mail</Label>
            <Input id="signatory-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="signatory-phone">Telefone</Label>
            <Input id="signatory-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          {editingId && (
            <Button variant="outline" onClick={cancelEdit} disabled={isSaving}>
              Cancelar edição
            </Button>
          )}
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Salvando…" : editingId ? "Salvar alterações" : "Adicionar"}
          </Button>
        </div>

        <div className="max-h-64 overflow-y-auto rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Papel</TableHead>
                <TableHead>Contato</TableHead>
                <TableHead className="w-0" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                    Carregando…
                  </TableCell>
                </TableRow>
              ) : !signatories || signatories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                    Nenhum signatário/testemunha cadastrado ainda.
                  </TableCell>
                </TableRow>
              ) : (
                signatories.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{SIGNATORY_ROLE_LABEL[s.role]}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {[s.email, s.phone].filter(Boolean).join(" · ") || "—"}
                    </TableCell>
                    <TableCell className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(s)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleDelete(s.id)}
                        disabled={deleteSignatory.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
